<template>
  <div class="range">
    <div
      ref="body"
      class="range__body"
      @mousedown="setKnobByClick">
      <div
        v-for="(knob, index) in knobsCount"
        ref="knobs"
        :key="knob"
        class="range__knob"
        data-ignore-click
        @mousedown="startDrag(index)"
        @touchstart.stop.prevent="startDrag(index, $event)" />
      <div
        ref="line"
        class="range__line" />
    </div>
    <div
      v-if="showScale"
      class="range__scale">
      <div
        v-for="scaleItem in scaleItems"
        :key="scaleItem.value"
        :style="{
          '--position': `${scaleItem.position}%`,
        }"
        :class="{
          'range__scale-item--active': knobsCount === 1
            ? scaleItem.value <= value[0]
            : scaleItem.value >= value[0] && scaleItem.value <= value[1],
        }"
        class="range__scale-item transition--color"
        @mousedown="setKnobByClick">
        {{ scaleItem.formattedValue }}
      </div>
    </div>
  </div>
</template>

<script>
/** прототип нового ренжа */

/*
шаблон для подключения

<ui-range
  v-model="values"
  :zones="zones" />
*/

/*
в компоненте где подключается

data() {
  return {
    values: [70, 140],
    zones: [
      {
        from: 50,
        to: 100,
        size: 70,
        step: 5,
      },
      {
        from: 100,
        to: 150,
        size: 30,
        step: 10,
      },
    ],
  };
},
*/

import {
  hasMouse,
  hasTouch,
  supportsPassiveEvent,
  transformBigNumber,
} from 'Root/helpers/tools';

/** моменты которые надо доделать
 *  - не работает с отрицательными числами (а надо?)
 *  */

export default {
  name: 'UiRange',

  props: {
    /**
     * значения ренжа в виде массива, от количества элементов будет зависеть количество ручек
     */
    value: {
      type: Array,
      required: true,
    },

    /**
     * массив с объектами зон следующего вида:
     [
       {
          from: 0, // с какого значения зона действует
          to: 20, // до какого значения зона действует
          size: 50, // процент "занимаемого" зоной места
          step: 1, // размер шага значений в этой зоне
       },
       {
          from: 20,
          to: 100,
          size: 50,
          step: 5,
       },
     ]
     */
    zones: {
      type: Array,
      required: true,
      validator: (val) => {
        let sumSize = 0;
        for (let i = 0; i < val.length; i++) {
          if ('from' in val[i] && 'to' in val[i] && val[i].size && val[i].step) {
            sumSize += +val[i].size;
          } else {
            return false;
          }
        }
        return Math.round(sumSize * 100) / 100 === 100;
      },
    },

    showScale: {
      type: Boolean,
      default: false,
    },

    scaleStepsCount: {
      type: Number,
      default: 6,
    },
  },

  data() {
    return {
      knobsCount: this.value.length,
      touchPos: { x: 0, y: 0 },

      // Было снаружи, но перенесено внутрь из-за смешивания от разных компонентов
      draggingKnobIndex: null,
      draggingKnobPosition: null,
      knobPositions: [],
      lastEmittedValues: [],
      lineBounding: null,
    };
  },

  computed: {
    borderValues() {
      return [
        this.zones[0].from,
        this.zones[this.zones.length - 1].to,
      ];
    },

    scaleItems() {
      if (!this.showScale) {
        return [];
      }

      const scaleStep = (this.borderValues[1] - this.borderValues[0]) / this.scaleStepsCount;
      const scaleItems = [];
      const scaleStepsCount = (this.scaleStepsCount > this.borderValues[1])
        ? this.borderValues[1] - 1
        : this.scaleStepsCount;

      scaleItems.push({
        value: this.borderValues[0],
        formattedValue: transformBigNumber(this.borderValues[0]),
        position: 0,
      });

      let i = 1;
      for (i; i < scaleStepsCount; i++) {
        const scaleValue = Math.floor(scaleStep * i + this.borderValues[0]);

        scaleItems.push({
          value: scaleValue,
          formattedValue: transformBigNumber(scaleValue),
          position: this.getPercentPositionFromValue(scaleValue),
        });
      }

      scaleItems.push({
        value: this.borderValues[1],
        formattedValue: transformBigNumber(this.borderValues[1]),
        position: 100,
      });
      return scaleItems;
    },
  },

  watch: {
    zones() {
      this.updateRangeState();
    },

    value: {
      handler(newValues) {
        let changed = false;

        if (newValues.length !== this.lastEmittedValues.length) {
          changed = true;
        } else {
          for (let i = 0; i < newValues.length; i++) {
            if (newValues[i] !== this.lastEmittedValues[i]) {
              changed = true;
              break;
            }
          }
        }

        if (newValues.length !== this.knobsCount) {
          this.knobsCount = newValues.length;
        }

        if (this.knobPositions.length > this.knobsCount) {
          this.knobPositions = this.knobPositions.slice(0, this.knobsCount);
        }

        if (!changed) {
          return;
        }

        this.updateRangeState();
      },
      deep: true,
    },
  },

  mounted() {
    this.updateRangeState();
    window.addEventListener('resize', this.updateRangeState);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateRangeState);
  },

  methods: {
    startDrag(knobIndex, event) {
      this.draggingKnobIndex = knobIndex;
      this.draggingKnobPosition = this.knobPositions[this.draggingKnobIndex] || 0;
      this.setLineBounding();

      if (hasTouch) {
        document.body.classList.add('no-scroll');
        this.touchStart(event);
        document.addEventListener('touchmove', this.touchMove, supportsPassiveEvent ? { passive: true } : false);
        document.addEventListener('touchend', this.endDrag, { once: true });
        document.addEventListener('touchcancel', this.endDrag, { once: true });
      }

      if (hasMouse) {
        document.addEventListener('mouseup', this.endDrag, { once: true });
        document.addEventListener('mousemove', this.mouseDrag);
      }
    },

    endDrag() {
      this.draggingKnobIndex = null;

      if (hasTouch) {
        document.body.classList.remove('no-scroll');
        document.removeEventListener('touchmove', this.touchMove);
      }

      if (hasMouse) {
        document.removeEventListener('mousemove', this.mouseDrag);
      }

      this.$emit('change', this.lastEmittedValues);
    },

    setLineBounding() {
      this.lineBounding = this.$refs.line.getBoundingClientRect();
    },

    mouseDrag(event) {
      const leftBorder = this.lineBounding.x;
      const rightBorder = this.lineBounding.x + this.lineBounding.width;
      const deltaX = +event.movementX;

      if (event.pageX > rightBorder) { // При вылете за границы ставим крайнюю точку
        this.validateAndMoveKnob(this.lineBounding.width - this.draggingKnobPosition);
      } else if (event.pageX < leftBorder) {
        this.validateAndMoveKnob(-this.draggingKnobPosition);
      } else {
        this.validateAndMoveKnob(deltaX);
      }
    },

    touchStart(event) {
      // если и мышка и тач одновременно иногда пролетает undefined
      if (!event) {
        return;
      }

      this.touchPos.x = event.touches[0].pageX;
    },

    touchMove(event) {
      const leftBorder = this.lineBounding.x;
      const rightBorder = this.lineBounding.x + this.lineBounding.width;
      const deltaX = event.touches[0].pageX - this.touchPos.x;
      this.touchPos.x = event.touches[0].pageX;

      if (event.touches[0].pageX > rightBorder) { // При вылете за границы ставим крайнюю точку
        this.validateAndMoveKnob(this.lineBounding.width - this.draggingKnobPosition);
      } else if (event.touches[0].pageX < leftBorder) {
        this.validateAndMoveKnob(-this.draggingKnobPosition);
      } else {
        this.validateAndMoveKnob(+deltaX);
      }
    },

    validateAndMoveKnob(deltaX) {
      // попытки улететь за край иногда блокировали установку ручки в последнее значение
      if (deltaX > 0 && this.draggingKnobPosition + deltaX > this.lineBounding.width) {
        deltaX = this.lineBounding.width - this.draggingKnobPosition;
      }

      const isValid = this.validateMovement(this.draggingKnobIndex, this.draggingKnobPosition + deltaX);
      if (isValid) {
        this.draggingKnobPosition += deltaX;
        this.moveKnob(this.draggingKnobIndex, this.draggingKnobPosition);
        this.emitValue();
      }
    },

    moveKnob(knobIndex, position) {
      this.knobPositions[knobIndex] = position;

      requestAnimationFrame(() => {
        const positionInPx = `${position}px`;
        this.$refs.knobs[knobIndex].style.transform = `translate(-50%, -50%) translateX(${positionInPx})`;

        if (knobIndex === 0) {
          this.$refs.line.style.setProperty('--first-position', positionInPx);
        } else if (knobIndex === this.knobsCount - 1) {
          this.$refs.line.style.setProperty('--last-position', positionInPx);
        }
      });
    },

    validateMovement(knobIndex, position) {
      const prevKnobIndex = knobIndex - 1;
      const nextKnobIndex = knobIndex + 1;
      const prevKnobPosition = this.knobPositions[prevKnobIndex] ?? false;
      const nextKnobPosition = this.knobPositions[nextKnobIndex] ?? false;
      const [minBorder, maxBorder] = [0, this.lineBounding.width];
      // скипаем проверку соседних ручек если мы едем в другую сторону
      const deltaX = position - this.knobPositions[knobIndex];

      const currentValue = this.getValueFromPosition(position);
      let prevValue = prevKnobPosition !== false ? this.getValueFromPosition(prevKnobPosition) : null;
      let nextValue = nextKnobPosition !== false ? this.getValueFromPosition(nextKnobPosition) : null;

      // наткнулись на предыдущую ручку
      if (deltaX < 0 && ((prevKnobPosition && position <= prevKnobPosition) || currentValue === prevValue)) {
        // TODO SKY-3398 оптимизировать
        let prevPosition = prevKnobPosition + deltaX;
        prevValue = currentValue;
        while (prevValue === currentValue && prevValue > this.borderValues[0]) {
          prevValue = this.getValueFromPosition(prevPosition);
          prevPosition -= 1;
        }

        const isPrevKnobMovable = this.validateMovement(prevKnobIndex, prevPosition);
        if (isPrevKnobMovable && currentValue !== prevValue) {
          this.moveKnob(prevKnobIndex, prevPosition);
          return true;
        }
        return false;
      }

      // наткнулись на следующую ручку
      if (deltaX > 0 && ((nextKnobPosition && position >= nextKnobPosition) || currentValue === nextValue)) {
        // TODO SKY-3398 оптимизировать
        let nextPosition = nextKnobPosition + deltaX;
        nextValue = currentValue;
        while (nextValue === currentValue && nextValue < this.borderValues[1]) {
          nextValue = this.getValueFromPosition(nextPosition);
          nextPosition += 1;
        }

        const isNextKnobMovable = this.validateMovement(nextKnobIndex, nextPosition);
        if (isNextKnobMovable && currentValue !== nextValue) {
          this.moveKnob(nextKnobIndex, nextPosition);
          return true;
        }

        return false;
      }

      return minBorder <= position && position <= maxBorder;
    },

    setKnobsByValue() {
      this.$refs.knobs.forEach((knob, index) => {
        this.moveKnob(index, this.getPercentPositionFromValue(this.value[index]) * (this.lineBounding.width / 100));
      });
    },

    setKnobByClick(event) {
      if (event.clientX > this.lineBounding.x + this.lineBounding.width || event.clientX < this.lineBounding.x) {
        return; // Тач на мобилке считается за клик, можно было выдернуть хваталку за границу
      }

      const position = event.pageX - this.lineBounding.left;
      let closestKnobIndex = null;
      let closestDistanceDelta = null;
      this.knobPositions.forEach((knobPosition, index) => {
        const positionDelta = Math.abs(knobPosition - position);
        if (closestKnobIndex === null || (closestDistanceDelta && positionDelta <= closestDistanceDelta)) {
          closestKnobIndex = index;
          closestDistanceDelta = positionDelta;
        }
      });

      this.moveKnob(closestKnobIndex, position);
      this.emitValue();
    },

    updateRangeState() {
      requestAnimationFrame(() => {
        this.setLineBounding();
        this.setKnobsByValue();
      });
    },

    getPercentPositionFromValue(value) {
      let positionPercent = null;
      this.zones.forEach((zone) => {
        if (value >= zone.to) {
          positionPercent += zone.size;
        } else if (value > zone.from) {
          positionPercent += (value - zone.from) * (zone.size / (zone.to - zone.from));
        }
      });

      return positionPercent;
    },

    getValueFromPosition(position) {
      let value = this.borderValues[0];
      let positionPercent = (position / this.lineBounding.width) * 100;

      this.zones.forEach((zone) => {
        if (positionPercent <= 0) {
          return;
        }

        if (positionPercent >= zone.size) {
          const zoneValue = zone.to - zone.from;
          value += Math.round(zoneValue / zone.step) * zone.step;
        } else if (positionPercent < zone.size) {
          const zoneValue = ((zone.to - zone.from) / zone.size) * positionPercent;
          value += Math.round(zoneValue / zone.step) * zone.step;
        }

        positionPercent -= zone.size;
      });

      return value;
    },

    emitValue() {
      let valuesChanged = false;
      const values = [];
      let i = 0;
      for (i; i < this.knobsCount; i++) {
        const value = this.getValueFromPosition(this.knobPositions[i]);
        const clampedValue = Math.min(Math.max(value, this.borderValues[0]), this.borderValues[1]);
        values.push(clampedValue);

        if (clampedValue !== this.lastEmittedValues[i]) {
          valuesChanged = true;
        }
      }

      if (valuesChanged) {
        this.lastEmittedValues = [...values];
        this.$emit('input', values);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.range {
  --line-bg-color: #{$color--basic-menu};

  user-select: none;
}

.range__body {
  position: relative;
  padding: 1.125em 0;
  cursor: pointer;
}

.range__line {
  height: .25em;
  background: var(--line-bg-color);
  position: relative;
  overflow: hidden;
}

.range__knob {
  position: absolute;
  top: 50%;
  left: 0;
  width: 2.5em;
  height: 2.5em;
  background-color: rgba($color--basic-blue, .25);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  cursor: grab;

  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: .5em;
    height: .5em;
    background-color: $color--basic-white;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    box-shadow: 0 0 0 .375em $color--basic-blue;
  }

  &:first-child:not(:nth-last-child(2)) ~ .range__line:before {
    background-color: $color--basic-blue;
    transform: translate(var(--first-position));
  }

  &:nth-last-child(2) + .range__line:after {
    background-color: var(--line-bg-color);
    transform: translate(var(--last-position));
  }

  &:first-child:nth-last-child(2) + .range__line:before {
    background-color: $color--basic-blue;
  }

  &:first-child:nth-last-child(2) + .range__line:after {
    background-color: var(--line-bg-color);
    transform: translate(var(--first-position));
  }
}

.range__line:before,
.range__line:after {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
}

.range__knob:active {
  cursor: grabbing;
}

.range__scale {
  position: relative;
  height: 15px;
}

.range__scale-item {
  position: absolute;
  left: var(--position);
  transform: translateX(-50%);
  font-size: $text-13px;
  color: $color--basic-dark-gray;
  font-weight: 500;
}

.range__scale-item--active {
  color: $color--basic-white;
}
</style>
